import { useState } from 'react'

import { Icon } from '@loadsmart/miranda-react'

import { type Thread } from 'assistant/domain/Thread'

import DeleteThreadDialog from './DeleteThreadDialog'
import { type useThreads } from './hooks/useThreads'
import RenameThreadDialog from './RenameThreadDialog'
import {
  StyledList,
  StyledListHeader,
  StyledNewChat,
  StyledNote,
  StyledRows,
  StyledSkeleton,
  StyledTextButton,
} from './ThreadList.styles'
import ThreadRow from './ThreadRow'

type ThreadListProps = {
  state: ReturnType<typeof useThreads>
  activeThreadId?: string
  onSelect: (threadId: string) => void
  onNewChat: () => void
  /** Called after a thread was deleted, so the page can leave it if it was open. */
  onDeleted: (threadId: string) => void
}

export default function ThreadList({
  state,
  activeThreadId,
  onSelect,
  onNewChat,
  onDeleted,
}: ThreadListProps) {
  const [renaming, setRenaming] = useState<Thread | null>(null)
  const [deleting, setDeleting] = useState<Thread | null>(null)

  return (
    <StyledList aria-label="Conversations">
      <StyledListHeader>
        <StyledNewChat type="button" onClick={onNewChat}>
          <Icon name="plus" size="16px" />
          New chat
        </StyledNewChat>
      </StyledListHeader>

      {state.isLoading && (
        <div aria-hidden="true">
          {[0, 1, 2].map(index => (
            <StyledSkeleton key={index} data-testid="thread-skeleton" />
          ))}
        </div>
      )}

      {state.isError && (
        <>
          <StyledNote>Couldn't load your conversations.</StyledNote>
          <StyledTextButton type="button" onClick={() => state.refetch()}>
            Try again
          </StyledTextButton>
        </>
      )}

      {!state.isLoading && !state.isError && state.threads.length === 0 && (
        <StyledNote>Your conversations will show up here.</StyledNote>
      )}

      {state.threads.length > 0 && (
        <StyledRows>
          {state.threads.map(thread => (
            <ThreadRow
              key={thread.id}
              thread={thread}
              active={thread.id === activeThreadId}
              onSelect={() => onSelect(thread.id)}
              onRename={() => setRenaming(thread)}
              onDelete={() => setDeleting(thread)}
            />
          ))}
        </StyledRows>
      )}

      {state.hasMore && (
        <StyledTextButton type="button" onClick={() => state.loadMore()}>
          Show more
        </StyledTextButton>
      )}

      {renaming && (
        <RenameThreadDialog
          thread={renaming}
          onSave={title => state.renameThread(renaming.id, title)}
          onClose={() => setRenaming(null)}
        />
      )}

      {deleting && (
        <DeleteThreadDialog
          thread={deleting}
          onConfirm={async () => {
            await state.deleteThread(deleting.id)
            onDeleted(deleting.id)
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </StyledList>
  )
}
